import { barParfumPrice } from "@/lib/bar-parfum";
import { createFileRoute, Link, useRouter, type ErrorComponentProps } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { submitEstimation } from "@/lib/devis-estimatifs.functions";
import { PageHero } from "@/components/page-hero";
import { DateField } from "@/components/date-field";

import heroImg from "@/assets/dj-mariage-equipment.jpg";
import { metaDesc } from "@/lib/seo-description";

import { ReviewsProof } from "@/components/reviews-proof";
export const Route = createFileRoute("/calculateur")({
  head: () => ({
    meta: [
      { title: "Calculateur de devis DJ instantané | DJ Val" },
      {
        name: "description",
        content:
          metaDesc("Estimez en 30 secondes le prix de votre DJ mariage dans le Vaucluse. Fourchette transparente selon la date, le lieu, le nombre d'invités et les options choisies.")
      },
      { property: "og:title", content: "Calculateur de devis DJ — DJ Val" },
      { property: "og:description", content: metaDesc("Une estimation en 30 secondes. Sans engagement.") },
      { property: "og:url", content: "https://dj-val.fr/calculateur" },
    ],
    links: [{ rel: "canonical", href: "https://dj-val.fr/calculateur" }],
  }),
  component: CalculateurPage,
  errorComponent: ({ error, reset }: ErrorComponentProps) => {
    const router = useRouter();
    return (
      <div className="min-h-dvh bg-background text-foreground p-8 pt-32">
        <p className="text-destructive">{error instanceof Error ? error.message : String(error)}</p>
        <button onClick={() => { router.invalidate(); reset(); }} className="mt-4 rounded bg-foreground px-4 py-2 text-background">Réessayer</button>
      </div>
    );
  },
  notFoundComponent: () => <div className="p-8 pt-32">Page introuvable</div>,
});

type EventType = "mariage" | "anniversaire" | "entreprise" | "gender_reveal" | "autre";

const TYPES: { id: EventType; label: string; base: number }[] = [
  { id: "mariage", label: "Mariage", base: 1490 },
  { id: "anniversaire", label: "Anniversaire", base: 690 },
  { id: "entreprise", label: "Entreprise / Séminaire", base: 990 },
  { id: "gender_reveal", label: "Gender Reveal", base: 290 },
  { id: "autre", label: "Autre événement", base: 790 },
];

const OPTIONS: { id: string; label: string; price: number }[] = [
  { id: "ceremonie", label: "Sonorisation cérémonie laïque", price: 180 },
  { id: "cocktail", label: "Sonorisation cocktail / vin d'honneur", price: 180 },
  { id: "photobooth", label: "Photobooth pro", price: 350 },
  { id: "karaoke", label: "Pack karaoké complet", price: 200 },
  { id: "lumiere_plus", label: "Pack lumière renforcé (mapping / par LED)", price: 280 },
  { id: "fumee_lourde", label: "Fumée lourde 1ère danse", price: 180 },
  { id: "spark", label: "Étincelles froides", price: 150 },
  { id: "guirlandes", label: "Guirlandes guinguette", price: 250 },
  { id: "video", label: "Vidéoprojection", price: 200 },
  { id: "bar_parfum", label: "Bar à parfum L'Atelier Olfactif (Nouveauté 2027, 14 €/invité, min. 50)", price: 0 },
];


const ZONES: { id: string; label: string; supplement: number }[] = [
  { id: "0-80", label: "0 – 80 km de Cavaillon (Avignon, Aix, Arles, Salon, Apt, Carpentras…) — inclus", supplement: 0 },
  { id: "80-120", label: "80 – 120 km (Nîmes, Marseille, Uzès…)", supplement: 90 },
  { id: "120+", label: "Plus de 120 km (sur devis)", supplement: 200 },
];

function CalculateurPage() {
  const submit = useServerFn(submitEstimation);

  const [type, setType] = useState<EventType>("mariage");
  const [date, setDate] = useState("");
  const [lieu, setLieu] = useState("");
  const [invites, setInvites] = useState(100);
  const [duree, setDuree] = useState(7);
  const [zone, setZone] = useState(ZONES[0].id);
  const [options, setOptions] = useState<string[]>([]);

  const [nom, setNom] = useState("");
  const [email, setEmail] = useState("");
  const [tel, setTel] = useState("");
  const [message, setMessage] = useState("");

  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const estim = useMemo(() => {
    const t = TYPES.find((x) => x.id === type)!;
    let base = t.base;
    // durée au-delà de 6h
    if (duree > 6) base += (duree - 6) * 110;
    // invités
    if (invites > 150) base += 150;
    if (invites > 250) base += 250;
    if (invites > 400) base += 350;
    // options
    const opt = options.reduce((s, id) => s + (id === "bar_parfum" ? barParfumPrice(invites) : OPTIONS.find((o) => o.id === id)?.price ?? 0), 0);
    // zone
    const z = ZONES.find((x) => x.id === zone)?.supplement ?? 0;
    const total = base + opt + z;
    // fourchette ±10%
    const min = Math.round((total * 0.95) / 10) * 10;
    const max = Math.round((total * 1.1) / 10) * 10;
    return { min, max };
  }, [type, duree, invites, options, zone]);

  const toggleOpt = (id: string) =>
    setOptions((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setSending(true);
    try {
      await submit({
        data: {
          type_event: TYPES.find((x) => x.id === type)!.label,
          date_event: date || null,
          lieu: lieu || null,
          nb_invites: invites,
          duree_heures: duree,
          options: options.map((id) => OPTIONS.find((o) => o.id === id)?.label ?? id),
          zone: ZONES.find((x) => x.id === zone)?.label ?? null,
          montant_min: estim.min,
          montant_max: estim.max,
          nom,
          email,
          telephone: tel || null,
          message: message || null,
        },
      });
      setSent(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erreur");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-background text-foreground">
      <PageHero
        image={heroImg}
        eyebrow="Calculateur en ligne"
        title={<>Estimez votre devis<span className="block italic mt-2">en 30 secondes</span></>}
        subtitle="Une fourchette de prix réaliste, transparente, sans engagement. Le devis définitif est validé par téléphone."
        primary={{ label: "Devis personnalisé", to: "/contactez-nous" }}
        secondary={{ label: "07 67 90 86 67", href: "tel:0767908667" }}
        height="compact"
      />
      <ReviewsProof />

      <section className="py-20 px-6">

        <div className="max-w-6xl mx-auto grid lg:grid-cols-[1fr_380px] gap-10">
          {/* Formulaire */}
          <div className="space-y-10 bg-card border border-foreground/10 p-8 md:p-10">
            {/* Type */}
            <fieldset>
              <legend className="font-serif-display text-2xl mb-5">Type d'événement</legend>
              <div className="grid sm:grid-cols-2 gap-2">
                {TYPES.map((t) => (
                  <button
                    type="button"
                    key={t.id}
                    onClick={() => setType(t.id)}
                    className={`border px-4 py-3 text-left text-sm transition ${
                      type === t.id
                        ? "border-[var(--gold)] bg-[var(--gold)]/10 text-foreground"
                        : "border-foreground/15 text-muted-foreground hover:border-foreground/40"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </fieldset>

            {/* Date + lieu */}
            <fieldset className="grid sm:grid-cols-2 gap-4">
              <div className="block">
                <DateField
                  value={date}
                  onChange={setDate}
                  id="calculateur-date-evenement"
                  label="Date de l'événement"
                  labelClassName="text-xs uppercase tracking-[0.25em] text-muted-foreground"
                  className="mt-2 w-full border border-foreground/15 bg-background px-3 py-2.5"
                />
              </div>

              <label className="block">
                <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Lieu / commune</span>
                <input
                  type="text"
                  value={lieu}
                  onChange={(e) => setLieu(e.target.value)}
                  placeholder="Domaine de l'Ermitage, Avignon…"
                  className="mt-2 w-full border border-foreground/15 bg-background px-3 py-2.5"
                />
              </label>
            </fieldset>

            {/* Invités + durée */}
            <fieldset className="grid sm:grid-cols-2 gap-6">
              <div>
                <div className="flex justify-between text-xs uppercase tracking-[0.25em] text-muted-foreground">
                  <span>Invités</span>
                  <span className="text-foreground">{invites}</span>
                </div>
                <input
                  type="range"
                  min={20}
                  max={500}
                  step={10}
                  value={invites}
                  onChange={(e) => setInvites(Number(e.target.value))}
                  className="w-full mt-3 accent-[var(--gold)]"
                />
              </div>
              <div>
                <div className="flex justify-between text-xs uppercase tracking-[0.25em] text-muted-foreground">
                  <span>Durée animation</span>
                  <span className="text-foreground">{duree} h</span>
                </div>
                <input
                  type="range"
                  min={3}
                  max={12}
                  step={1}
                  value={duree}
                  onChange={(e) => setDuree(Number(e.target.value))}
                  className="w-full mt-3 accent-[var(--gold)]"
                />
              </div>
            </fieldset>

            {/* Zone */}
            <fieldset>
              <legend className="font-serif-display text-2xl mb-5">Zone de déplacement</legend>
              <div className="space-y-2">
                {ZONES.map((z) => (
                  <label
                    key={z.id}
                    className={`flex items-center justify-between border px-4 py-3 cursor-pointer text-sm transition ${
                      zone === z.id ? "border-[var(--gold)] bg-[var(--gold)]/10" : "border-foreground/15"
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="zone"
                        checked={zone === z.id}
                        onChange={() => setZone(z.id)}
                        className="accent-[var(--gold)]"
                      />
                      {z.label}
                    </span>
                    {z.supplement > 0 && (
                      <span className="text-muted-foreground">+{z.supplement} €</span>
                    )}
                  </label>
                ))}
              </div>
            </fieldset>

            {/* Options */}
            <fieldset>
              <legend className="font-serif-display text-2xl mb-5">Options</legend>
              <div className="grid sm:grid-cols-2 gap-2">
                {OPTIONS.map((o) => {
                  const active = options.includes(o.id);
                  return (
                    <button
                      type="button"
                      key={o.id}
                      onClick={() => toggleOpt(o.id)}
                      className={`flex justify-between gap-3 border px-4 py-3 text-left text-sm transition ${
                        active
                          ? "border-[var(--gold)] bg-[var(--gold)]/10"
                          : "border-foreground/15 hover:border-foreground/40"
                      }`}
                    >
                      <span>{o.label}</span>
                      <span className="text-muted-foreground whitespace-nowrap">+{o.id === "bar_parfum" ? barParfumPrice(invites) : o.price} €</span>
                    </button>
                  );
                })}
              </div>
            </fieldset>
          </div>

          {/* Résumé sticky */}
          <aside className="lg:sticky lg:top-28 self-start">
            <div className="bg-foreground text-background p-8">
              <p className="text-[10px] uppercase tracking-[0.3em] opacity-70">Votre estimation</p>
              <p className="font-serif-display text-4xl mt-4 leading-tight">
                {estim.min.toLocaleString("fr-FR")} – {estim.max.toLocaleString("fr-FR")} €
              </p>
              <p className="text-xs opacity-70 mt-2">Net de TVA (franchise art. 293 B CGI)</p>
              <span className="block h-px bg-[var(--gold)] w-12 my-6" />
              <ul className="text-sm space-y-1.5 opacity-90">
                <li>· {TYPES.find((t) => t.id === type)?.label}</li>
                <li>· {invites} invités · {duree} h</li>
                <li>· {ZONES.find((z) => z.id === zone)?.label.split("(")[0].trim()}</li>
                {options.length > 0 && <li>· {options.length} option{options.length > 1 ? "s" : ""}</li>}
              </ul>
              <p className="text-[11px] opacity-60 mt-6 leading-relaxed">
                Fourchette indicative basée sur les prestations standards.
                Le devis final est validé par téléphone après échange.
              </p>
            </div>

            {/* Formulaire envoi */}
            {!sent ? (
              <form onSubmit={onSubmit} className="bg-card border border-foreground/10 border-t-0 p-8 space-y-4">
                <p className="font-serif-display text-xl">Recevoir cette estimation</p>
                <p className="text-xs text-muted-foreground">
                  Je vous rappelle sous 24 h pour affiner et confirmer le devis définitif.
                </p>
                <input
                  required
                  type="text"
                  placeholder="Nom"
                  value={nom}
                  onChange={(e) => setNom(e.target.value)}
                  className="w-full border border-foreground/15 bg-background px-3 py-2.5 text-sm"
                />
                <input
                  required
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border border-foreground/15 bg-background px-3 py-2.5 text-sm"
                />
                <input
                  type="tel"
                  placeholder="Téléphone (recommandé)"
                  value={tel}
                  onChange={(e) => setTel(e.target.value)}
                  className="w-full border border-foreground/15 bg-background px-3 py-2.5 text-sm"
                />
                <textarea
                  placeholder="Précisions (facultatif)"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  className="w-full border border-foreground/15 bg-background px-3 py-2.5 text-sm"
                />
                {err && <p className="text-sm text-destructive">{err}</p>}
                <button
                  type="submit"
                  disabled={sending}
                  className="w-full bg-[var(--gold)] text-[var(--ink)] py-3 text-sm uppercase tracking-[0.25em] disabled:opacity-50"
                >
                  {sending ? "Envoi…" : "Envoyer mon estimation"}
                </button>
                <p className="text-[10px] text-muted-foreground text-center">
                  Aucun engagement. Vos données ne sont utilisées que pour vous recontacter.
                </p>
              </form>
            ) : (
              <div className="bg-card border border-foreground/10 border-t-0 p-8 text-center">
                <p className="font-serif-display text-2xl">Merci {nom.split(" ")[0]} ✦</p>
                <span className="gold-rule mx-auto !w-12 my-5" />
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Votre demande est bien enregistrée. Je vous rappelle sous 24 h
                  pour affiner cette estimation et préparer le devis définitif.
                </p>
                <Link
                  to="/realisations"
                  className="inline-block mt-6 text-xs uppercase tracking-[0.25em] border border-foreground/20 px-5 py-2.5 hover:bg-foreground hover:text-background transition"
                >
                  Découvrir mes réalisations
                </Link>
              </div>
            )}
          </aside>
        </div>
      </section>
    </div>
  );
}
